import styled from 'styled-components';
import { CSSVars } from '@/styles/styles.config';

export const StyledToolHero = styled.section`
  background: url(/assets/hero-bg.webp);
  background-size: 100% 100%;
  background-repeat: no-repeat;
  background-position: center 0%;
  color: ${CSSVars.white};
  padding: 140px 20px 140px;
  text-align: center;
  position: relative;
  overflow: hidden;

  h1 {
    font-size: 1.875rem;
    font-weight: 300;
    margin-bottom: 20px;
    line-height: 1.15;
    position: relative;
    color: ${CSSVars.white};

    .highlight {
      font-weight: 600;
      background: linear-gradient(90deg, #a5a8ff, #ff979c);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
  }

  p {
    font-size: ${CSSVars.fontSizeMD};
    color: rgba(255, 255, 255, 0.8);
    max-width: 580px;
    margin: 0 auto;
    line-height: 1.7;
    font-weight: 300;
    position: relative;
  }

  @media screen and (min-width: ${CSSVars.breakpointMD}) {
    padding: 160px 40px 160px;
    h1 { font-size: ${CSSVars.fontSize2XL}; }
    p  { font-size: ${CSSVars.fontSizeLG}; }
  }

  @media screen and (min-width: ${CSSVars.breakpointLG}) {
    padding: 180px 40px 180px;
    h1 { font-size: ${CSSVars.fontSize3XL}; }
  }
`;

export const StyledToolBody = styled.div`
  background: ${CSSVars.gray50};
  padding: 60px 20px 80px;

  @media screen and (min-width: ${CSSVars.breakpointLG}) {
    padding: 80px 40px 100px;
  }
`;

export const StyledCalculator = styled.div`
  max-width: 860px;
  margin: 0 auto;
  background: ${CSSVars.white};
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 20px;
  padding: 28px 20px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);

  h2 {
    font-size: ${CSSVars.fontSizeLG};
    font-weight: 700;
    color: ${CSSVars.black};
    margin-bottom: 28px;
  }

  @media screen and (min-width: ${CSSVars.breakpointMD}) {
    padding: 48px;
    h2 {
      font-size: ${CSSVars.fontSizeXL};
      margin-bottom: 32px;
    }
  }
`;

export const StyledFieldsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;

  @media screen and (min-width: ${CSSVars.breakpointMD}) {
    grid-template-columns: 1fr 1fr;
    gap: 0 24px;
  }
`;

export const StyledFormGroup = styled.div`
  margin-bottom: 24px;

  label {
    display: block;
    font-size: ${CSSVars.fontSizeSM};
    font-weight: 600;
    color: ${CSSVars.gray700};
    margin-bottom: 8px;
  }

  input, select, textarea {
    width: 100%;
    padding: 13px 16px;
    font-size: ${CSSVars.fontSizeMD};
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 10px;
    color: ${CSSVars.black};
    background: ${CSSVars.white};
    transition: border-color 0.2s, box-shadow 0.2s;
    font-family: ${CSSVars.secondaryFont};

    &::placeholder {
      color: ${CSSVars.gray400};
    }

    &:focus {
      border-color: ${CSSVars.blue500};
      box-shadow: 0 0 0 3px rgba(76, 79, 255, 0.1);
      outline: none;
    }
  }

  textarea {
    resize: vertical;
    min-height: 80px;
  }

  select {
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%23888' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 16px center;
    padding-right: 40px;
    cursor: pointer;
  }

  .hint {
    font-size: ${CSSVars.fontSizeXS};
    color: ${CSSVars.gray500};
    margin-top: 6px;
    line-height: 1.5;
  }
`;

export const StyledFullWidth = styled.div`
  @media screen and (min-width: ${CSSVars.breakpointMD}) {
    grid-column: 1 / -1;
  }
`;

export const StyledCalculateButton = styled.button`
  width: 100%;
  padding: 15px 24px;
  background: ${CSSVars.blue500};
  color: ${CSSVars.white};
  font-size: ${CSSVars.fontSizeMD};
  font-weight: 700;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.3s, transform 0.1s;
  font-family: ${CSSVars.primaryFont};
  letter-spacing: 0.01em;

  &:hover {
    background: linear-gradient(270deg, ${CSSVars.pink300} 0%, ${CSSVars.blue500} 100%);
  }

  &:active {
    transform: scale(0.99);
  }
`;

export const StyledResult = styled.div<{ status: 'poor' | 'fair' | 'good' }>`
  max-width: 860px;
  margin: 32px auto 0;
  scroll-margin-top: 80px;
  background: ${CSSVars.white};
  border: 1px solid ${({ status }) =>
    status === 'good'
      ? 'rgba(74, 222, 128, 0.4)'
      : status === 'fair'
      ? 'rgba(252, 211, 77, 0.4)'
      : 'rgba(248, 113, 113, 0.4)'};
  border-radius: 20px;
  padding: 28px 20px;
  box-shadow: 0 4px 32px
    ${({ status }) =>
      status === 'good'
        ? 'rgba(74, 222, 128, 0.12)'
        : status === 'fair'
        ? 'rgba(252, 211, 77, 0.12)'
        : 'rgba(248, 113, 113, 0.12)'};

  .result-label {
    font-size: ${CSSVars.fontSizeSM};
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: ${CSSVars.gray500};
    margin-bottom: 12px;
    text-align: center;
  }

  .result-number {
    font-size: 3rem;
    font-weight: 700;
    line-height: 1;
    margin-bottom: 14px;
    text-align: center;
    color: ${({ status }) =>
      status === 'good' ? '#16a34a' : status === 'fair' ? '#b45309' : '#dc2626'};

    @media screen and (min-width: ${CSSVars.breakpointMD}) {
      font-size: ${CSSVars.fontSize4XL};
    }
  }

  .result-badge {
    display: block;
    text-align: center;
    margin-bottom: 24px;

    span {
      display: inline-block;
      padding: 5px 16px;
      border-radius: 20px;
      font-size: ${CSSVars.fontSizeXS};
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.08em;
      background: ${({ status }) =>
        status === 'good'
          ? 'rgba(22, 163, 74, 0.1)'
          : status === 'fair'
          ? 'rgba(180, 83, 9, 0.1)'
          : 'rgba(220, 38, 38, 0.1)'};
      color: ${({ status }) =>
        status === 'good' ? '#16a34a' : status === 'fair' ? '#b45309' : '#dc2626'};
    }
  }

  @media screen and (min-width: ${CSSVars.breakpointMD}) {
    padding: 40px 48px;
  }
`;

export const StyledCTACategory = styled.div`
  margin-top: 28px;

  h3 {
    font-size: ${CSSVars.fontSizeMD};
    font-weight: 700;
    color: ${CSSVars.black};
    margin-bottom: 14px;
    padding-bottom: 10px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  }
`;

export const StyledCTAOption = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  background: ${CSSVars.gray50};
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 10px;
  margin-bottom: 10px;
  transition: border-color 0.2s;

  &:hover {
    border-color: rgba(76, 79, 255, 0.3);
  }

  .cta-text {
    font-size: ${CSSVars.fontSizeSM};
    color: ${CSSVars.black};
    line-height: 1.5;
    font-weight: 400;
    flex: 1;
  }

  .copy-btn {
    flex-shrink: 0;
    padding: 6px 14px;
    background: ${CSSVars.blue500};
    color: ${CSSVars.white};
    font-size: ${CSSVars.fontSizeXS};
    font-weight: 700;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    transition: background 0.3s;
    font-family: ${CSSVars.primaryFont};

    &:hover {
      background: linear-gradient(270deg, ${CSSVars.pink300} 0%, ${CSSVars.blue500} 100%);
    }
  }
`;

export const StyledWidgetsSection = styled.section`
  max-width: 1000px;
  margin: 72px auto 0;

  h2 {
    font-size: 1.5rem;
    font-weight: 700;
    color: ${CSSVars.black};
    margin-bottom: 10px;
    text-align: center;
    line-height: 1.2;

    @media screen and (min-width: ${CSSVars.breakpointMD}) {
      font-size: ${CSSVars.fontSize2XL};
    }
  }

  .subtitle {
    text-align: center;
    color: ${CSSVars.gray600};
    font-size: ${CSSVars.fontSizeMD};
    margin-bottom: 48px;
    font-weight: 300;
  }
`;

export const StyledWidgetGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;

  @media screen and (min-width: ${CSSVars.breakpointMD}) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

export const StyledCtaBanner = styled.section`
  background: ${CSSVars.white};
  padding: 60px 20px 80px;

  @media screen and (min-width: ${CSSVars.breakpointMD}) {
    padding: 80px 30px 100px;
  }

  .banner-inner {
    max-width: 1000px;
    margin: 0 auto;
    background: linear-gradient(135deg, #4c4fff 0%, #6b8cff 100%);
    border-radius: 20px;
    padding: 48px 32px;
    text-align: center;
    color: ${CSSVars.white};

    @media screen and (min-width: ${CSSVars.breakpointMD}) {
      padding: 64px 48px;
    }
  }

  h2 {
    font-size: ${CSSVars.fontSizeLG};
    font-weight: 700;
    margin-bottom: 16px;
    line-height: 1.3;

    @media screen and (min-width: ${CSSVars.breakpointMD}) {
      font-size: ${CSSVars.fontSize2XL};
    }
  }

  p {
    font-size: ${CSSVars.fontSizeSM};
    color: rgba(255, 255, 255, 0.85);
    max-width: 520px;
    margin: 0 auto 28px;
    line-height: 1.7;
    font-weight: 300;

    @media screen and (min-width: ${CSSVars.breakpointMD}) {
      font-size: ${CSSVars.fontSizeMD};
    }
  }

  .banner-cta {
    display: inline-block;
    padding: 14px 32px;
    background: ${CSSVars.white};
    color: #3538cd;
    font-size: ${CSSVars.fontSizeSM};
    font-weight: 700;
    border-radius: 10px;
    text-decoration: none;
    transition: transform 0.2s, box-shadow 0.2s;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
    }
  }
`;

export const StyledRelatedTools = styled.section`
  max-width: 1000px;
  margin: 72px auto 0;

  h2 {
    font-size: 1.5rem;
    font-weight: 700;
    color: ${CSSVars.black};
    margin-bottom: 10px;
    text-align: center;
    line-height: 1.2;

    @media screen and (min-width: ${CSSVars.breakpointMD}) {
      font-size: ${CSSVars.fontSize2XL};
    }
  }

  .subtitle {
    text-align: center;
    color: ${CSSVars.gray600};
    font-size: ${CSSVars.fontSizeMD};
    margin-bottom: 48px;
    font-weight: 300;
  }
`;

export const StyledRelatedToolGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;

  @media screen and (min-width: ${CSSVars.breakpointMD}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media screen and (min-width: ${CSSVars.breakpointLG}) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

export const StyledRelatedToolCard = styled.a`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 28px 24px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 18px;
  text-decoration: none;
  color: inherit;
  background: ${CSSVars.white};
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
  text-align: center;
  transition: border-color 0.2s, box-shadow 0.2s, transform 0.2s;

  &:hover {
    border-color: rgba(76, 79, 255, 0.3);
    box-shadow: 0 6px 24px rgba(76, 79, 255, 0.1);
    transform: translateY(-2px);
  }

  .tool-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: rgba(76, 79, 255, 0.08);
    color: ${CSSVars.blue500};

    svg {
      width: 24px;
      height: 24px;
    }
  }

  h3 {
    font-size: ${CSSVars.fontSizeMD};
    font-weight: 700;
    color: ${CSSVars.black};
    line-height: 1.3;
  }

  p {
    font-size: ${CSSVars.fontSizeSM};
    color: ${CSSVars.gray600};
    line-height: 1.65;
    flex: 1;
    font-weight: 300;
  }

  .tool-cta {
    padding: 9px 18px;
    background: ${CSSVars.blue500};
    color: ${CSSVars.white};
    font-size: ${CSSVars.fontSizeSM};
    font-weight: 700;
    border-radius: 8px;
    transition: background 0.3s;

    &:hover {
      background: linear-gradient(270deg, ${CSSVars.pink300} 0%, ${CSSVars.blue500} 100%);
    }
  }
`;

export const StyledWidgetCard = styled.a`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 28px 24px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 16px;
  text-decoration: none;
  color: inherit;
  background: ${CSSVars.white};
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
  transition: border-color 0.2s, box-shadow 0.2s, transform 0.2s;

  &:hover {
    border-color: rgba(76, 79, 255, 0.3);
    box-shadow: 0 6px 24px rgba(76, 79, 255, 0.1);
    transform: translateY(-3px);
  }

  .widget-header {
    display: flex;
    align-items: center;
    gap: 12px;

    img {
      width: 44px;
      height: 44px;
      object-fit: contain;
      border-radius: 10px;
      background: ${CSSVars.gray50};
    }

    h3 {
      font-size: ${CSSVars.fontSizeMD};
      font-weight: 700;
      color: ${CSSVars.black};
      line-height: 1.3;
    }
  }

  p {
    font-size: ${CSSVars.fontSizeSM};
    color: ${CSSVars.gray600};
    line-height: 1.65;
    flex: 1;
    font-weight: 300;
  }

  .widget-cta {
    display: inline-block;
    padding: 10px 18px;
    background: ${CSSVars.blue500};
    color: ${CSSVars.white};
    font-size: ${CSSVars.fontSizeSM};
    font-weight: 700;
    border-radius: 8px;
    text-align: center;
    transition: background 0.3s;

    &:hover {
      background: linear-gradient(270deg, ${CSSVars.pink300} 0%, ${CSSVars.blue500} 100%);
    }
  }
`;
